import React from 'react';
import { StandardTableActions, ColoredBadge, StatusBadgeStylePicker } from '../../../components/ui';

import { Column } from '../../../components/DataTable/DataTable';
import { TaskStatus } from './useData';
import { FieldConfig } from '../../../components/generic/fieldRenderers';
import { FormLayout } from '../../../components/generic/FormLayoutRenderer';
import { formatDate as formatDateGlobal, APP_CONFIG } from '../../../config/appConfig';
import { normalizeHexColor, parseStatusColorConfig, serializeStatusColorConfig } from '../../../utils/statusColor';

const DEFAULT_TASK_STATUS_COLOR = '#64748b';

const getTaskStatusColor = (color?: string): string =>
  normalizeHexColor(parseStatusColorConfig(color).backgroundColor) ?? DEFAULT_TASK_STATUS_COLOR;

const renderTaskStatusBadge = (label?: string, color?: string) => (
  <ColoredBadge label={label || '-'} color={color || DEFAULT_TASK_STATUS_COLOR} />
);

export const moduleConfig = {
  module: {
    title: 'Task Status',
    description: 'Manage task status',
    permissionModuleKey: 'task_status',
    route: {
      list: '/task_status',
      new: '/task_status/new',
      detail: (id: number) => `/task_status/${id}`,
      edit: (id: number) => `/task_status/${id}/edit`,
    },
    api: {
      list: '/p-crm-task-status',
      detail: (id: number) => `/p-crm-task-status/${id}`,
    },
  },
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions,
  },
  search: {
    placeholder: 'Search task status...',
  },
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic',
      icon: '🔍',
      fields: ['ts_descr'],
    },
  ],
  filters: {
    fields: [
      {
        key: 'ts_descr',
        label: 'Description',
        placeholder: 'Type to filter description...',
        type: 'text' as const,
        getValue: (item: TaskStatus) => item.ts_descr,
        options: [],
      },
    ],
    applyFilter: (items: TaskStatus[], filters: { ts_descr: string[] | string }) => {
      return items.filter((item) => {
        const descrFilter = filters.ts_descr;
        if (typeof descrFilter === 'string' && descrFilter.trim() !== '') {
          const value = (item.ts_descr || '').toLowerCase();
          if (!value.includes(descrFilter.toLowerCase())) return false;
        } else if (Array.isArray(descrFilter) && descrFilter.length > 0) {
          const value = (item.ts_descr || '').toLowerCase();
          const matches = descrFilter.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }
        return true;
      });
    },
  },
  toolbar: {
    actions: [
      {
        key: 'add',
        icon: 'add',
        title: 'Add New Task Statu',
        showTitle: false,
        variant: 'primary' as const,
        href: '/task_status/new',
      },
    ],
  },
  table: {
    actionsPosition: 'left' as const,
    onRowClick: (item: TaskStatus) => {
      window.location.href = `/task_status/${item.id}`;
    },
    keyExtractor: (item: TaskStatus) => item.id,
  },
  emptyStates: {
    noData: 'Get started by creating a new task statu.',
    noSearchResults: 'No task status found matching your search criteria.',
    noFilterResults: 'No task status match the selected filters.',
  },
  delete: {
    title: 'Delete Task Statu',
    message: 'Are you sure you want to delete this task statu? This action cannot be undone.',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Failed to delete task statu. Please try again.',
    handler: async (id: number) => {
      const { deleteTaskStatus } = await import('../../../services/mutations');
      await deleteTaskStatus(id);
    },
  },
  utils: {
    formatDate: (dateString?: string) => {
      if (!dateString) return '-';
      return formatDateGlobal(dateString);
    },
  },
  detail: {
    subtitle: 'Task Statu Details',
    notFoundTitle: 'Task Statu not found',
    notFoundMessage: 'The task statu you\'re looking for doesn\'t exist.',
    notFoundBackText: 'Back to Task Status',
    defaultTitle: 'Unnamed Task Statu',
    getTitle: (item: TaskStatus) => {
      return item.ts_descr || `Task Statu #${item.id}`;
    },
  },
  form: {
    new: {
      title: 'New Task Statu',
      subtitle: 'Add a new task statu',
      submitText: 'Create Task Statu',
    },
    edit: {
      title: 'Edit Task Statu',
      subtitle: 'Update task statu information',
      submitText: 'Update Task Statu',
    },
    cancelText: 'Cancel',
    loadingText: 'Loading task statu...',
    errorMessage: 'Failed to save task statu. Please try again.',
    create: async (data: Partial<TaskStatus>) => {
      const { createTaskStatus } = await import('../../../services/mutations');
      return await createTaskStatus(data);
    },
    update: async (id: number, data: Partial<TaskStatus>) => {
      const { updateTaskStatus } = await import('../../../services/mutations');
      return await updateTaskStatus(id, data);
    },
  },
  fields: [
    {
      name: 'ts_descr',
      label: 'Description',
      type: 'text',
      required: true,
      placeholder: 'Enter task statu description',
      validation: {
        required: true,
        minLength: 1,
        maxLength: 255,
      },
    },
    {
      name: 'ts_color',
      label: 'Badge Style',
      type: 'custom',
      required: true,
      helperText: 'Pick the background color and how the badge text should be rendered.',
      validation: {
        required: true,
      },
      customRender: ({ value, error, formData, setMultipleFieldValues }) => (
        <StatusBadgeStylePicker
          backgroundColor={parseStatusColorConfig(typeof value === 'string' ? value : DEFAULT_TASK_STATUS_COLOR).backgroundColor}
          textColorMode={parseStatusColorConfig(typeof value === 'string' ? value : DEFAULT_TASK_STATUS_COLOR).textColorMode}
          error={error}
          helperText="This will be used in task status badges and task status selection."
          previewLabel={formData?.ts_descr || 'Preview'}
          onChange={(backgroundColor, textColorMode) => {
            setMultipleFieldValues?.({
              ts_color: serializeStatusColorConfig(backgroundColor, textColorMode),
            });
          }}
        />
      ),
    },
  ] as FieldConfig[],
  formLayout: {
    mode: 'default',
    sections: [
      {
        id: 'basic',
        title: 'Task Statu Information',
        icon: '📝',
        fields: ['ts_descr', 'ts_color'],
        columns: 2,
        collapsible: false,
      },
    ],
  } as FormLayout,
};

export const getColumns = (formatDate: (dateString?: string) => string): Column<TaskStatus>[] => [
  {
    key: 'id',
    header: 'ID',
    sortable: true,
    render: (value) => (
      <span className="text-sm text-gray-900">{value || '-'}</span>
    ),
  },
  {
    key: 'ts_descr',
    header: 'Description',
    sortable: true,
    render: (value, row) => renderTaskStatusBadge(value, row.ts_color),
  },
  {
    key: 'ts_color',
    header: 'Color',
    sortable: true,
    render: (value, row) => {
      const resolvedColor = getTaskStatusColor(row.ts_color);
      return (
        <div className="flex items-center gap-2">
          <span
            className="h-4 w-4 rounded-full border border-black/10"
            style={{ backgroundColor: resolvedColor }}
          />
          <span className="text-sm text-gray-700">{resolvedColor}</span>
        </div>
      );
    },
  },
];

export const getDetailSections = (
  item: TaskStatus,
  formatDate: (dateString?: string) => string
) => {
  return [
    {
      title: 'Basic Information',
      icon: '📝',
      fields: [
        {
          label: 'Description',
          value: renderTaskStatusBadge(item.ts_descr, item.ts_color),
        },
        {
          label: 'Color',
          value: (
            <div className="flex items-center gap-2">
              <span
                className="h-4 w-4 rounded-full border border-black/10"
                style={{ backgroundColor: getTaskStatusColor(item.ts_color) }}
              />
              <span>{getTaskStatusColor(item.ts_color)}</span>
            </div>
          ),
        },
      ],
    },
  ];
};

export const getStatusBadges = (item: TaskStatus) => {
  return renderTaskStatusBadge(item.ts_descr, item.ts_color);
};

export const getInitialFormData = (): Partial<TaskStatus> => {
  return {
    ts_descr: '',
    ts_color: serializeStatusColorConfig(DEFAULT_TASK_STATUS_COLOR, 'auto'),
  };
};

export const mapItemToFormData = (item: TaskStatus): Partial<TaskStatus> => {
  return {
    ts_descr: item.ts_descr || '',
    ts_color: item.ts_color || serializeStatusColorConfig(DEFAULT_TASK_STATUS_COLOR, 'auto'),
  };
};

export const getToolbarIcon = (iconName: string): React.ReactNode => {
  switch (iconName) {
    case 'add':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
      );
    default:
      return null;
  }
};

export const getTableActions = (
  item: TaskStatus,
  onDelete: (id: number) => void,
  _colors?: unknown,
  _listContext?: unknown,
  options?: { canDelete?: boolean; canEdit?: boolean }
): React.ReactNode => {
  const canDelete = options?.canDelete !== false;
  const canEdit = options?.canEdit !== false;
  return (
    <StandardTableActions
      viewTo={moduleConfig.module.route.detail(item.id)}
      editTo={moduleConfig.module.route.edit(item.id)}
      onDelete={() => onDelete(item.id)}
      canEdit={canEdit}
      canDelete={canDelete}
    />
  );
};

